import { checkForUpdate } from './swUpdate'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { applyUpdate, useUpdateAvailable } from '@/lib/appUpdate'
import { RefreshCw, WifiOff } from '@/design-system/icons'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { Button, LOTTIE, LottieScene } from '@/design-system'
import { useT } from '@/i18n'

const subscribe = (cb: () => void) => {
  window.addEventListener('online', cb)
  window.addEventListener('offline', cb)
  return () => {
    window.removeEventListener('online', cb)
    window.removeEventListener('offline', cb)
  }
}


export const useOnline = () => useSyncExternalStore(subscribe, () => navigator.onLine, () => true)

export function NetworkBanner() {
  const { t } = useT()
  const online = useOnline()
  if (online) return null
  return (
    <div role="status" className="animate-slide-up flex items-center justify-center gap-3 border-b border-warning/30 bg-warning-soft px-4 py-1 text-sm font-medium text-warning">
      <LottieScene src={LOTTIE.noConnection} size={44} className="-my-1" />
      <WifiOff size={14} aria-hidden />
      {t('shell.offlineBanner')}
    </div>
  )
}

/** A new version is out: a friendly card invites the user to tap and install it (nothing reloads by itself). */
export function UpdatePrompt() {
  const { locale } = useT()
  const ar = locale === 'ar'
  const build = useUpdateAvailable()
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  /*
   * "لما ينزل ابديت وضح ف الابديت تفاصيل الابديت" (owner, 2026-09-26). The lines come from the build that is being
   * offered, so they describe what tapping Update actually brings. A missing or unreadable file changes nothing:
   * the card falls back to the sentence it always had.
   */
  const [notes, setNotes] = useState<{ text: string; sha: string }[]>([])
  useEffect(() => {
    if (!build) return
    let alive = true
    void fetch('/release.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { items?: { text?: unknown; sha?: unknown }[] } | null) => {
        if (!alive || !d?.items) return
        setNotes(d.items.map((i) => ({ text: String(i.text ?? '').trim(), sha: String(i.sha ?? '') })).filter((i) => i.text))
      })
      .catch(() => undefined)
    return () => { alive = false }
  }, [build])
  useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => checkForUpdate(registration), 10 * 60_000)
    },
  })
  if (!build) return null
  return (
    <div className="update-card fixed inset-x-3 z-[60] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-border bg-surface-raised p-3 shadow-xl md:inset-x-auto md:end-5 md:bottom-5" role="status">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: 'linear-gradient(140deg,#d2272a,#ae1a1c 55%,#7c1012)' }}>
        <RefreshCw size={20} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-fg-strong">{ar ? 'يتوفر تحديث جديد لنظام Aliens OS' : 'A new Aliens OS update ✨'}</span>
        {/* what is actually in it, in the words each change was committed with (public/release.json, written at build) */}
        {notes.length ? (
          <span className="mt-0.5 block text-xs text-fg-muted">
            {notes.slice(0, open ? 8 : 2).map((n) => <span key={n.sha} className="block truncate">• {n.text}</span>)}
            {notes.length > 2 && (
              <button type="button" className="mt-0.5 font-medium text-brand-text hover:underline" onClick={() => setOpen((o) => !o)}>
                {open ? (ar ? 'إخفاء' : 'Show less') : ar ? `و${notes.length - 2} أخرى` : `${notes.length - 2} more`}
              </button>
            )}
          </span>
        ) : (
          <span className="block text-xs text-fg-muted">{ar ? 'اضغط تحديث لتثبيته والاستفادة من الجديد' : 'Tap update to install it and enjoy what is new'}</span>
        )}
      </span>
      <Button size="sm" variant="primary" loading={busy} onClick={() => { setBusy(true); void applyUpdate() }}>
        {ar ? 'تحديث' : 'Update'}
      </Button>
    </div>
  )
}
